"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { FileText, RefreshCw, ArrowLeft } from "lucide-react";
import { useTranslation } from "../hooks/useTranslation";

const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

export default function FormSelection({ onSelectForm, onPrevious, className }) {
  const { t } = useTranslation();
  const [availableForms, setAvailableForms] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFormLoading, setIsFormLoading] = React.useState(false);
  const [selectedFormId, setSelectedFormId] = React.useState(null);

  // Initial fallback data
  const initialFormsList = [
    {
      id: "form_1",
      title: "Form 1",
      description: "Created: 2024-01-15",
      icon: FileText,
      form_code: 'DEFAULT_FORM_001',
      production_line: 'Default Line',
      total_fields: 10,
      total_points: 100
    },
    {
      id: "form_2", 
      title: "Form 2",
      description: "Created: 2024-02-08",
      icon: FileText,
      form_code: 'DEFAULT_FORM_002',
      production_line: 'Default Line',
      total_fields: 15,
      total_points: 150
    }
  ];

  // Fetch audit forms from API
  const fetchAuditForms = async () => {
    setIsLoading(true);
    try {
      const apiBase = process.env.REACT_APP_BASE_API_URL
      const response = await fetch(`${apiBase}/api/frontend/audit-forms/list/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
        
        // Check if response is successful and has results
        if (data.success && Array.isArray(data.results)) {
          const auditForms = data.results;
          
          // Transform API data to match FormSelection component structure
          const transformedForms = auditForms.map((form, index) => ({
            id: `form_${form.audit_form_id}`, // Form-based ID pattern
            title: form.form_name || `Form ${form.audit_form_id}`,
            description: form.created_at 
              ? `Created: ${new Date(form.created_at).toLocaleDateString()}` 
              : 'Recently created',
            icon: FileText,
            // Additional data for potential use
            audit_form_id: form.audit_form_id,
            form_code: form.form_code,
            form_name_italian: form.form_name_italian,
            production_line: typeof form.production_line === 'object' 
              ? (form.production_line?.line_name || form.production_line?.name || `Line ID: ${form.production_line?.line_id}`)
              : form.production_line,
            product: typeof form.product === 'object' 
              ? (form.product?.model_number || form.product?.product_name || `Product ID: ${form.product?.product_id}`)
              : form.product,
            total_fields: form.total_fields_count || 0,
            total_points: form.total_possible_points || 0,
            passing_threshold: form.passing_threshold,
            is_published: form.is_published,
            created_at: form.created_at,
            updated_at: form.updated_at
          }));
          
          console.log('Transformed forms for selection:', transformedForms);
          setAvailableForms(transformedForms);
        } else {
          console.error('Invalid API response structure:', data);
          // Use initial data as fallback
          setAvailableForms(initialFormsList);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch audit forms:', response.status, response.statusText, errorText);
        // Use initial data as fallback
        setAvailableForms(initialFormsList);
      }
    } catch (error) {
      console.error('Error fetching audit forms:', error);
      // Use initial data as fallback
      setAvailableForms(initialFormsList);
    } finally {
      setIsLoading(false);
    }
  };

  // Load audit forms when component mounts
  React.useEffect(() => {
    fetchAuditForms();
  }, []);

  const handleFormSelection = (form) => {
    setIsFormLoading(true);
    setSelectedFormId(form.id);
    console.log('Selected form:', form);
    
    // Simulate loading and pass the complete form object
    setTimeout(() => {
      onSelectForm?.(form);
      setIsFormLoading(false);
      setSelectedFormId(null);
    }, 750);
  };

  const handleRefresh = () => {
    fetchAuditForms();
  };

  return (
    <section className={cn("min-h-screen flex items-center justify-center p-4 bg-gray-50", className)}>
      <motion.div 
        initial={{
          opacity: 0,
          y: 20
        }} 
        animate={{
          opacity: 1,
          y: 0
        }} 
        transition={{
          duration: 0.5,
          ease: "easeOut"
        }} 
        className="w-full max-w-5xl"
      >
        <Card className="w-full rounded-xl shadow-lg bg-white border-0 overflow-hidden">
          <CardContent className="p-8">
            {/* Header with navigation and refresh */}
            <div className="flex items-center justify-between mb-6">
              {onPrevious && (
                <Button 
                  variant="ghost" 
                  onClick={onPrevious}
                  className="flex items-center gap-2 text-gray-600 hover:text-[#0098cb] p-2"
                  disabled={isFormLoading}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('forms.back')}
                </Button>
              )}
              
              <div className="flex-1" />
              
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isLoading || isFormLoading}
                className="flex items-center gap-2 border-[#0098cb] text-[#0098cb] hover:bg-[#0098cb] hover:text-white"
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                {isLoading ? t('forms.loading') : t('forms.refresh')}
              </Button>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#0098cb] text-center mb-8">
              {t('forms.title')}
            </h1>

            {/* Loading state */}
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0098cb] mr-3"></div>
                <span className="text-gray-600">{t('forms.loading_forms')}</span>
              </div>
            ) : (
              <ScrollArea className="h-[500px] w-full pr-4">
                {availableForms.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {availableForms.map((form, index) => (
                      <motion.div 
                        key={form.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        className="cursor-pointer" 
                        onClick={() => !isFormLoading && handleFormSelection(form)} 
                        onKeyDown={e => {
                          if ((e.key === 'Enter' || e.key === ' ') && !isFormLoading) {
                            handleFormSelection(form);
                          }
                        }} 
                        tabIndex={0} 
                        aria-label={t('forms.select_form').replace('{0}', form.title)}
                      >
                        <Card className={cn(
                          "h-full p-6 flex flex-col items-center justify-center text-center border-gray-200 hover:border-[#0098cb] hover:shadow-md transition-all duration-200 rounded-lg relative",
                          selectedFormId === form.id && "border-[#0098cb] shadow-md",
                          isFormLoading && selectedFormId !== form.id && "opacity-50"
                        )}>
                          {/* Loading overlay for selected form */}
                          {isFormLoading && selectedFormId === form.id && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-[#0098cb] animate-bounce [animation-delay:-0.3s]"></div>
                                <div className="w-3 h-3 rounded-full bg-[#0098cb] animate-bounce [animation-delay:-0.15s]"></div>
                                <div className="w-3 h-3 rounded-full bg-[#0098cb] animate-bounce"></div>
                              </div>
                            </div>
                          )}

                          <div className="mb-4 p-3 bg-[#0098cb]/10 rounded-full">
                            <form.icon className="h-8 w-8 text-[#0098cb]" />
                          </div>
                          
                          <h3 className="font-semibold text-lg text-gray-800 mb-2">{form.title}</h3>
                          <p className="text-sm text-gray-500 mb-3">{form.description}</p>
                          
                          {/* Additional form details */}
                          <div className="space-y-1 text-xs text-gray-400 text-center">
                            {form.form_code && (
                              <p>{t('forms.code')}: {form.form_code}</p>
                            )}
                            {form.total_fields > 0 && (
                              <p>{t('forms.fields')}: {form.total_fields} | {t('forms.points')}: {form.total_points}</p>
                            )}
                            {form.production_line && (
                              <p className="truncate max-w-full">{t('forms.line')}: {form.production_line}</p>
                            )}
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[350px] text-gray-500">
                    <FileText className="h-16 w-16 mb-4 opacity-40" />
                    <h3 className="text-xl font-medium mb-2">{t('forms.no_forms_available')}</h3>
                    <p className="text-sm text-center mb-4">
                      {t('forms.no_forms_desc')}<br />
                      {t('forms.contact_admin')}
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleRefresh}
                      className="border-[#0098cb] text-[#0098cb] hover:bg-[#0098cb] hover:text-white"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('forms.retry')}
                    </Button>
                  </div>
                )}
              </ScrollArea>
            )}

            {/* Global loading overlay */}
            {isFormLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20 rounded-xl">
                <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 rounded-full bg-[#0098cb] animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 rounded-full bg-[#0098cb] animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 rounded-full bg-[#0098cb] animate-bounce"></div>
                  </div>
                  <span className="text-gray-700 font-medium">{t('forms.loading_form')}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </section>
  );
}